import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DangerousConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Texte exact que l'utilisateur doit taper pour activer le bouton (ex: "SUPPRIMER") */
  challenge: string;
  /** Liste préformatée des conséquences (rendu en bullets) */
  consequences?: string[];
  /** Champ "motif" obligatoire pour audit log */
  requireReason?: boolean;
  confirmLabel?: string;
  loading?: boolean;
  /** Appelé avec le motif si requireReason */
  onConfirm: (reason?: string) => Promise<void> | void;
}

export function DangerousConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  challenge,
  consequences = [],
  requireReason = false,
  confirmLabel,
  loading = false,
  onConfirm,
}: DangerousConfirmDialogProps): JSX.Element {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  const ready = typed.trim() === challenge.trim() && (!requireReason || reason.trim().length >= 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b-destructive/30 bg-destructive/5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <DialogTitle className="text-destructive">{title}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          {consequences.length > 0 && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-destructive">
                Cette action va :
              </p>
              <ul className="space-y-1 pl-4 text-xs text-destructive">
                {consequences.map((c, i) => (
                  <li key={i} className="list-disc">
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {requireReason && (
            <div className="space-y-1.5">
              <Label htmlFor="reason">
                Motif <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Justifiez cette action (10 caractères min, journalisé dans audit_log)…"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="challenge">
              Tapez <code className="rounded bg-muted px-1 font-mono text-xs">{challenge}</code>{' '}
              pour confirmer :
            </Label>
            <Input
              id="challenge"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              className="font-mono"
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            disabled={!ready || loading}
            onClick={async () => {
              await onConfirm(requireReason ? reason : undefined);
            }}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel ?? 'Confirmer la suppression'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
